/**
 * template 命令：Excel 模板提取。
 *   template extract   提取 Excel 模板版式骨架（行高/列宽/合并/样式/数据位）→ JSON
 *   template to-ir     骨架 JSON → 报表 IR 基础版式（无损搬入合并/样式/文本，杜绝列合并丢失）
 *   template suggest   骨架 JSON + 字段列表 → 字段绑定建议（AI 辅助决策）
 *
 * 定位：Excel 模板仅提供版式（几何/样式/文本），数据来自服务端现有数据源体系；
 * 提取结果（模板骨架 JSON）供 LLM 做语义字段绑定，确认映射表后进入 generate 流程。
 * to-ir 是 extract → generate 之间的「无损转换器」：LLM 若手工搬移 cells 极易漏掉
 * 无文本的合并主格（列合并丢失），故骨架一律先经 to-ir 生成基础版式再补字段绑定。
 * suggest 为无标签数据位生成字段建议，综合知识库/样式/类型/邻近等多个信号。
 */
import { mkdirSync, readFileSync, writeFileSync } from 'node:fs';
import * as path from 'node:path';
import { Command } from 'commander';
import { extractTemplateFromBuffer } from '../core/template/xlsx.js';
import { skeletonToIr } from '../core/template/toIr.js';
import { batchScoreDataCells } from '../core/template/scorer.js';
import { dedupeListBindings } from '../core/template/dedupeListBindings.js';
import { applyConfigDir, readTemplateExtractConfig } from '../core/config.js';
import { withBudget, getBudgetCharger } from '../core/budget.js';
import { getLogger } from '../core/logger.js';

const log = getLogger('cli:template');

export function registerTemplate(program: Command) {
  const tpl = program.command('template').description('Excel 模板提取（版式骨架 → JSON，供 AI 做字段绑定）');

  tpl
    .command('extract')
    .description('提取 Excel 模板版式骨架（行高/列宽/合并/样式/数据位）→ JSON')
    .requiredOption('--in <file>', '输入的 .xlsx 模板文件')
    .requiredOption('--out <file>', '输出的模板骨架 JSON 文件')
    .option('--sheet <name>', '指定工作表名（缺省取第一个）')
    .option('--config-dir <dir>', '配置查找目录（含 .env / baskreport.config.json）')
    .option('--max-rows <n>', '只输出前 N 行（大模板控制 token）；缺省取 baskreport.config.json 的 template.maxRows，再回退 100')
    .option('--max-cols <n>', '只输出前 N 列；缺省取 config 的 template.maxCols，再回退 30')
    .option('--max-text <n>', '单元格文本截断长度；缺省取 config 的 template.maxText，再回退 30')
    .action(withBudget(async (opts) => {
      try {
        applyConfigDir(opts);
        // 优先级：CLI 显参 > baskreport.config.json 的 template 段 > 内置默认（100/30/30）
        const tplCfg = readTemplateExtractConfig();
        const maxRows = opts.maxRows != null ? Number(opts.maxRows) : (tplCfg.maxRows ?? 100);
        const maxCols = opts.maxCols != null ? Number(opts.maxCols) : (tplCfg.maxCols ?? 30);
        const maxText = opts.maxText != null ? Number(opts.maxText) : (tplCfg.maxText ?? 30);
        const buf = readFileSync(opts.in);
        const out = extractTemplateFromBuffer(buf, { sheet: opts.sheet, maxRows, maxCols, maxText });
        const outPath = path.resolve(opts.out);
        mkdirSync(path.dirname(outPath), { recursive: true });
        writeFileSync(outPath, JSON.stringify(out, null, 2), 'utf-8');
        getBudgetCharger().chargeText(JSON.stringify(out, null, 2)); // 通道 B：落盘文本计入预算
        log.info(`已写入: ${outPath}（${out.cells.length} 个有效格, ${out.dataRegions.length} 个数据区, maxRows=${maxRows}, maxCols=${maxCols}）`);
      } catch (e: any) {
        log.error(`template extract 失败：${e?.message ?? e}`, e);
        process.exitCode = 1;
      }
    }));;

  tpl
    .command('to-ir')
    .description('骨架 JSON → 报表 IR 基础版式（无损搬入全部合并/样式/文本，杜绝列合并丢失）')
    .requiredOption('--in <file>', '输入的模板骨架 JSON（template extract 的产物）')
    .requiredOption('--out <file>', '输出的报表 IR JSON（基础版式，供 LLM 做字段绑定后 generate）')
    .option('--name <name>', '报表名（缺省取骨架 sheet 名）')
    .action(withBudget(async (opts) => {
      try {
        const skeleton = JSON.parse(readFileSync(opts.in, 'utf-8'));
        const ir = skeletonToIr(skeleton);
        if (opts.name) ir.name = opts.name;
        const outPath = path.resolve(opts.out);
        mkdirSync(path.dirname(outPath), { recursive: true });
        writeFileSync(outPath, JSON.stringify(ir, null, 2), 'utf-8');
        getBudgetCharger().chargeText(JSON.stringify(ir, null, 2)); // 通道 B：落盘文本计入预算
        log.info(`已写入: ${outPath}（${ir.cells.length} 个版式格, ${ir.cells.filter((c) => (c.colspan ?? 1) > 1).length} 处列合并, ${ir.cells.filter((c) => (c.rowspan ?? 1) > 1).length} 处行合并, source=template）`);
      } catch (e: any) {
        log.error(`template to-ir 失败：${e?.message ?? e}`, e);
        process.exitCode = 1;
      }
    }));;

  tpl
    .command('suggest')
    .description('骨架 JSON + 字段列表 → 字段绑定建议（无标签数据位，综合知识库/样式/类型/邻近）')
    .requiredOption('--in <file>', '输入的模板骨架 JSON（template extract 的产物）')
    .requiredOption('--fields <fields>', '可用字段列表，逗号分隔（如 "amount,date,quantity"）')
    .option('--out <file>', '输出的绑定建议 JSON 文件（缺省输出到标准输出）')
    .option('--top-n <n>', '每个数据位返回前 N 个建议（缺省 5）')
    .option('--dict <path>', '绑定知识库 JSON 路径（缺省取 baskreport.config.json 的 bindings.dictPath；无则空知识库）')
    .option('--user-reports-dir <dir>', '用户报表/范例目录（可选；缺省取 baskreport.config.json 的 bindings.examplesDir，动态提取 XML→IR→绑定知识）')
    .option('--alias-dir <dir>', '数据集字段别名目录（可选；缺省取 baskreport.config.json 的 bindings.aliasDir，实时补充 label→字段 强映射知识）')
    .action(withBudget(async (opts) => {
      try {
        applyConfigDir(opts);
        const skeleton = JSON.parse(readFileSync(opts.in, 'utf-8'));
        const availableFields = opts.fields.split(',').map((f: string) => f.trim());
        const topN = opts.topN ? Number(opts.topN) : 5;

        // 0. 读取 bindings 配置（bindings.examplesDir / bindings.dictPath）
        const { readBindingsConfig } = await import('../core/config.js');
        const bindingsCfg = readBindingsConfig();

        // 1. 创建知识库加载器（内置静态 JSON 已废弃，dict 完全来自 config 或动态提取）
        const { BindingDictLoader } = await import('../core/template/index.js');
        const dictPath = opts.dict || bindingsCfg.dictPath || '';
        const loader = new BindingDictLoader(dictPath);

        // 2. 加载用户报表 / 范例目录（动态提取 XML→IR→绑定知识）
        const userReportsDir = opts.userReportsDir || bindingsCfg.examplesDir;
        if (userReportsDir) {
          log.info(`📂 从 ${userReportsDir} 动态提取绑定知识...`);
          await loader.loadUserReports(userReportsDir);
        }

        // 2.1 加载数据集字段别名目录（bindings.aliasDir → label→字段 强映射，实时补充）
        const aliasDir = opts.aliasDir || bindingsCfg.aliasDir;
        if (aliasDir) {
          log.info(`📂 从 ${aliasDir} 加载字段别名知识...`);
          await loader.loadAliases(aliasDir);
        }

        // 3. 打分（注入动态提取的 loader，确保知识库信号生效）
        const { batchScoreDataCells } = await import('../core/template/index.js');
        const suggestions = batchScoreDataCells({
          cells: skeleton.cells,
          blocks: skeleton.blocks,
          availableFields,
          topN,
          loader,
        });

        // 转换为更易读的格式
        const output: Record<string, unknown> = {};
        let kbUsed = false;
        let kbHitCount = 0;
        let kbTopPickCount = 0;
        let userAnchorPositions = 0; // 参考报表直接命中（锚定）的数据位数量
        let userAnchorHitCount = 0; // 锚定建议的累计条数
        for (const [key, sug] of suggestions) {
          output[key] = sug.map((s) => {
            // 命中知识库：knowledgeBase 信号 > 0（即该字段确实来自 binding-dict 的建议）
            const hit = s.signals.knowledgeBase > 0;
            if (hit) {
              kbUsed = true;
              kbHitCount += 1;
              // 数据位的第一名建议是否来自知识库
              if (sug[0] === s) kbTopPickCount += 1;
            }
            // 参考报表直接命中：reason 含「参考报表直接命中」（来自 scorer 的锚定逻辑）
            const isUserAnchor = s.reason.includes('参考报表直接命中');
            if (isUserAnchor) {
              userAnchorPositions += 1;
              userAnchorHitCount += 1;
            }
            return {
              field: s.field,
              confidence: (s.confidence * 100).toFixed(1) + '%',
              reason: s.reason.join(', '),
              userAnchor: isUserAnchor,
              signals: {
                knowledgeBase: (s.signals.knowledgeBase * 100).toFixed(0) + '%',
                style: (s.signals.style * 100).toFixed(0) + '%',
                typeMatch: (s.signals.typeMatch * 100).toFixed(0) + '%',
                proximity: (s.signals.proximity * 100).toFixed(0) + '%',
              },
            };
          });
        }

        const result = {
          sheet: skeleton.sheet,
          dataPositions: Object.keys(output).length,
          kbUsed,
          kbHitCount,
          kbTopPickCount,
          userAnchorPositions,
          userAnchorHitCount,
          suggestions: output,
          timestamp: new Date().toISOString(),
          knowledgeBaseStats: loader.getStats(),
        };

        if (opts.out) {
          const outPath = path.resolve(opts.out);
          mkdirSync(path.dirname(outPath), { recursive: true });
          writeFileSync(outPath, JSON.stringify(result, null, 2), 'utf-8');
          getBudgetCharger().chargeText(JSON.stringify(result, null, 2)); // 通道 B：落盘文本计入预算
          log.info(`已写入: ${outPath}（${Object.keys(output).length} 个数据位的建议）`);
        } else {
          console.log(JSON.stringify(result, null, 2));
        }
      } catch (e: any) {
        log.error(`template suggest 失败：${e?.message ?? e}`, e);
        process.exitCode = 1;
      }
    }));;

  tpl
    .command('dedupe')
    .description('对已绑定字段的报表 IR 做列表（明细表）字段绑定去重：逐行重复的 dataset 绑定压成单锚行 + expand:"down"（策略 A，仅依赖字段重复，零样式推断）')
    .requiredOption('--in <file>', '输入已绑定字段的报表 IR JSON')
    .requiredOption('--out <file>', '输出去重后的报表 IR JSON')
    .option('--expand-down', '为锚行补充/expand:"down"（缺省开启）', true)
    .action(withBudget(async (opts) => {
      try {
        const ir = JSON.parse(readFileSync(opts.in, 'utf-8'));
        const stats = {} as any;
        const deduped = dedupeListBindings(ir, stats);
        if (opts.expandDown) {
          // 为保留下来的明细绑定锚行补 expand:"down"（若尚缺）
          const bound = new Set<string>();
          for (const c of deduped.cells) {
            if (c.content?.kind === 'dataset' && !c.content.aggregate && !(c.content.conditions?.length)) {
              bound.add(`${c.row}@${c.col}`);
            }
          }
          for (const c of deduped.cells) {
            if (bound.has(`${c.row}@${c.col}`) && !c.expand) c.expand = 'down';
          }
        }
        const outPath = path.resolve(opts.out);
        mkdirSync(path.dirname(outPath), { recursive: true });
        writeFileSync(outPath, JSON.stringify(deduped, null, 2), 'utf-8');
        getBudgetCharger().chargeText(JSON.stringify(deduped, null, 2)); // 通道 B：落盘文本计入预算
        log.info(`已去重: ${outPath}（列表区域 ${stats.listRegions} 个，删除重复绑定 ${stats.removedBindings} 个，dataset 绑定 ${stats.beforeBindings} → ${stats.afterBindings}，裁剪空白重复行 ${stats.removedRows} 行，rowCount ${ir.rowCount} → ${deduped.rowCount}）`);
      } catch (e: any) {
        log.error(`template dedupe 失败：${e?.message ?? e}`, e);
        process.exitCode = 1;
      }
    }));

  tpl
    .command('mine-dict')
    .description('从范例目录提取绑定知识库（XML → parseReport → IR → 绑定知识；内置静态 JSON 已废弃）')
    .option('--examples-dir <dir>', '范例/示例目录（缺省取 baskreport.config.json 的 bindings.examplesDir，再回退 src/examples/sample-center）')
    .option('--out <file>', '输出的 binding-dict.json 路径（缺省取 baskreport.config.json 的 bindings.dictPath；未配置则只打印统计不落盘）')
    .option('--max-rows <n>', '大模板只取前 N 行（缺省 100）')
    .option('--config-dir <dir>', '配置查找目录（含 .env / baskreport.config.json）')
    .action(withBudget(async (opts) => {
      try {
        applyConfigDir(opts);
        const { readBindingsConfig } = await import('../core/config.js');
        const bindingsCfg = readBindingsConfig();
        const examplesDir = opts.examplesDir || bindingsCfg.examplesDir || 'src/examples/sample-center';
        const outPath = opts.out || bindingsCfg.dictPath || '';
        const maxRows = opts.maxRows ? Number(opts.maxRows) : 100;

        const { mineBindingDict } = await import('../scripts/mine-binding-dict.js');
        await mineBindingDict(examplesDir, outPath ? path.resolve(outPath) : '', maxRows);
      } catch (e: any) {
        log.error(`template mine-dict 失败：${e?.message ?? e}`, e);
        process.exitCode = 1;
      }
    }));;
}
